import React from 'react';
import { CreditCard, TrendingDown, Gift, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useScrollAnimation, useStaggerAnimation } from '../hooks/useScrollAnimation';

const ProblemSection = () => {
  const { t, isRTL } = useLanguage();
  const [headerRef, headerVisible] = useScrollAnimation();
  const [cardsRef, visibleCards] = useStaggerAnimation(4, 150);

  const problems = [
    { 
      icon: CreditCard, 
      color: 'from-[#FF3B30] to-[#FF6B6B]',
      bgColor: 'bg-[#FF3B30]/10',
      ...t.problem.problems[0]
    },
    { 
      icon: TrendingDown, 
      color: 'from-[#FF9500] to-[#FFCC00]',
      bgColor: 'bg-[#FF9500]/10',
      ...t.problem.problems[1]
    },
    { 
      icon: Gift, 
      color: 'from-[#5856D6] to-[#AF52DE]',
      bgColor: 'bg-[#5856D6]/10',
      ...t.problem.problems[2]
    },
    { 
      icon: Clock, 
      color: 'from-[#007AFF] to-[#5AC8FA]',
      bgColor: 'bg-[#007AFF]/10',
      ...t.problem.problems[3]
    }
  ];

  return (
    <section className="py-20 md:py-24 bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-14 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-[#FF3B30]/10 rounded-full mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full animate-pulse"></div>
            <span className="text-[11px] font-semibold text-[#FF3B30] uppercase tracking-wide font-inter">
              {t.problem.badge}
            </span>
          </div>
          <h2 className="font-dm text-3xl md:text-4xl lg:text-[42px] font-semibold tracking-tight text-[#1D1D1F] mb-4 leading-tight">
            {t.problem.title}
          </h2>
          <p className="text-lg text-[#6e6e73] font-inter max-w-2xl mx-auto">
            {t.problem.subtitle}
          </p>
        </div>

        {/* Problem Cards Grid */}
        <div ref={cardsRef} className="grid md:grid-cols-2 gap-5">
          {problems.map((problem, index) => (
            <div 
              key={index} 
              className={`group bg-white rounded-2xl p-6 border border-[#E5E5E5]/60 hover:border-[#E5E5E5] transition-all duration-500 hover-lift card-shine ${
                visibleCards.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${isRTL ? 'text-right' : ''}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className={`flex gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {/* Icon */}
                <div className={`w-12 h-12 ${problem.bgColor} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <problem.icon className="w-6 h-6 text-[#1D1D1F]" />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-dm text-lg font-semibold text-[#1D1D1F] mb-2">
                    {problem.title}
                  </h3>
                  <p className="text-[#6e6e73] font-inter text-[15px] leading-relaxed mb-4">
                    {problem.desc}
                  </p>
                  
                  {/* Stat */}
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                    <span className={`text-2xl font-dm font-bold bg-gradient-to-r ${problem.color} bg-clip-text text-transparent`}>
                      {problem.stat}
                    </span>
                    <span className="text-[13px] text-[#86868b] font-inter">
                      {problem.statLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
